// Cliente del upscaling de video (Topaz vía Replicate).
//
// Flujo async en 2 pasos contra atlas-backend (ex webhooks n8n
// upscale_video_start/_status, ADR-2026-10-23-lo-ultimo-de-n8n-pasa-al-backend):
//   1) START → encola la ejecución y devuelve su id como `prediction_id`.
//   2) STATUS → se polea hasta `done` (sube el mp4 a `video-project-assets`) o
//      `error`.
//
// El upscaling es LENTO (minutos), por eso el poll es 15s / ~20min de margen.
// Ver ADR-596 y docs/superpowers/specs/2026-07-23-upscale-video-design.md.
import { supabase } from './supabase';
import { atlasBackendFetch } from './atlasBackend';

export type UpscaleResolution = '720p' | '1080p' | '4k';
export type UpscaleStatus = 'processing' | 'done' | 'error';

export interface VideoUpscaleParams {
  userId: string;
  companyId: string;
  projectId: string;
  /** Id del clip/asset de origen (trazabilidad: viaja en datos_generacion). */
  clipId: string;
  /** Signed URL (descargable) del video a mejorar. */
  videoUrl: string;
  /** Duración del video en segundos (para el costo/logging). */
  durationSec: number;
  /** Resolución destino del modelo Topaz (define calidad y costo). */
  targetResolution: UpscaleResolution;
}

export interface VideoUpscaleResult {
  predictionId: string;
  /** Storage path del video mejorado dentro de `video-project-assets`. */
  path: string;
  /** Signed URL lista para reproducir el resultado. */
  signedUrl: string;
}

const BUCKET = 'video-project-assets';
const POLL_INTERVAL_MS = 15000;
const POLL_MAX_ATTEMPTS = 80; // ~20 min de margen (Topaz es lento)

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

/**
 * Dispara el upscaling y polea el status hasta que termina. Devuelve el path +
 * signed URL del video mejorado. Lanza Error si falla o si hace timeout.
 * `onStatus` reporta las transiciones (para la UI).
 */
export async function upscaleVideo(
  params: VideoUpscaleParams,
  onStatus?: (s: UpscaleStatus) => void,
  /** Apenas el backend acepta el trabajo (ya se cobró): quien llama guarda el id para
   *  retomarlo si se cierra el editor (ADR-2026-11-03-los-procesos-se-retoman-al-volver). */
  onIniciado?: (predictionId: string) => void,
): Promise<VideoUpscaleResult> {
  // ── 1) START ──────────────────────────────────────────────────────────
  // user/company salen del JWT; el backend no los lee del body.
  const startRes = await atlasBackendFetch('/v1/videos/upscale/start', {
    project_id: params.projectId,
    clip_id: params.clipId,
    video_url: params.videoUrl,
    duration_sec: params.durationSec,
    target_resolution: params.targetResolution,
  });
  // El cuerpo se lee ANTES de mirar el status: el 402 de créditos y los 4xx/5xx
  // traen el mensaje para el usuario en `message`.
  const startRaw = await startRes.text();
  let startJson: {
    prediction_id?: string;
    status?: string;
    message?: string;
    error?: string;
  };
  try {
    startJson = JSON.parse(startRaw);
  } catch {
    throw new Error(`Error al iniciar el upscaling (${startRes.status}).`);
  }
  // El START puede fallar por créditos insuficientes u otro motivo: en ese caso
  // viene status:"error" con message (no viene prediction_id).
  if (startJson.status === 'error' || !startJson.prediction_id) {
    throw new Error(
      startJson.message || startJson.error || 'No se pudo iniciar el upscaling.',
    );
  }
  const predictionId = startJson.prediction_id;
  onIniciado?.(predictionId);
  onStatus?.('processing');
  return seguirUpscale(predictionId, onStatus);
}

/**
 * Sigue un upscaling ya arrancado hasta que termina (también para retomarlo al abrir el
 * editor). Devuelve el path + signed URL del video mejorado; lanza si falla o vence.
 */
export async function seguirUpscale(
  predictionId: string,
  onStatus?: (s: UpscaleStatus) => void,
): Promise<VideoUpscaleResult> {
  // ── 2) POLL STATUS ────────────────────────────────────────────────────
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);
    let st: { status?: string; path?: string; message?: string; error?: string };
    try {
      const stRes = await atlasBackendFetch('/v1/videos/upscale/status', {
        prediction_id: predictionId,
      });
      if (!stRes.ok) continue; // reintentar el poll
      st = await stRes.json();
    } catch {
      continue; // error de red puntual → reintentar
    }

    if (st.status === 'done' && st.path) {
      const { data: urlData } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(st.path, 3600);
      if (!urlData?.signedUrl) {
        throw new Error('Video mejorado pero no se pudo obtener la URL.');
      }
      onStatus?.('done');
      return { predictionId, path: st.path, signedUrl: urlData.signedUrl };
    }
    if (st.status === 'error') {
      onStatus?.('error');
      throw new Error(st.message || st.error || 'El upscaling del video falló.');
    }
    // status === 'processing' → seguir poleando
  }

  throw new Error('El upscaling del video tardó demasiado. Reintentá.');
}
